import React, { ReactElement, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import { TFunction } from 'i18next';
import { useHistory } from 'react-router-dom';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import { Classroom, ClassroomRoleClassification } from 'types/schema-generated-types';
import MoreVertMenu from 'components/@common/MoreVertMenu';
import { ActionOption, ClassroomStatus } from 'types';
import ProductIconList from 'components/@common/ProductIconList';
import StatusChip from 'components/@common/StatusChip';
import BreakableTypography from 'components/@common/BreakableTypography';

const useStyles = makeStyles(theme => {
  return {
    container: {
      padding: theme.spacing(5),
      paddingTop: 0
    },
    table: {
      maxWidth: 'unset !important'
    },
    tableBody: {
      background: 'white'
    },
    tableRow: {
      width: 30,
      cursor: 'pointer'
    },
    avatarContainer: {
      display: 'flex',
      justifyContent: 'center'
    },
    avatar: {
      height: 44,
      width: 44
    },
    productImage: {
      height: 26,
      width: 26
    },
    tableActionColumn: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingRight: theme.spacing(2)
    },
    actionButton: {
      height: 30
    }
  };
});

export interface ClassesAndStudentsListViewContainerProps {
  classrooms: Classroom[];
  actionOptions?: Array<ActionOption>;
}

const ClassesAndStudentsListViewContainer: React.FC<ClassesAndStudentsListViewContainerProps> = ({
  classrooms,
  actionOptions
}) => {
  const classes = useStyles();
  const { t } = useTranslation('ClassesAndStudentsListViewContainer');
  const history = useHistory();

  const getColumns: (t: TFunction) => ColumnProps<any>[] = useCallback(
    (t: TFunction) => [
      {
        id: 'name',
        label: t('Class'),
        getValue(item): ReactElement {
          return (
            <div key={`${item.id}_col_1`}>
              <BreakableTypography>{item.name}</BreakableTypography>
            </div>
          );
        },
        sortKey: 'name',
        sortable: true
      },
      {
        id: 'educator',
        label: t('Educator'),
        getValue(item): ReactElement {
          return (
            <div key={`${item.id}_col_2`}>
              <Typography>{item.educator}</Typography>
            </div>
          );
        },
        sortKey: 'educator',
        sortable: true
      },
      {
        id: 'coeducators',
        label: t('Co-Educator'),
        getValue(item): ReactElement {
          return (
            <div key={`${item.id}_col_3`}>
              <Typography>{item.coeducators}</Typography>
            </div>
          );
        },
        sortKey: 'coeducators',
        sortable: true
      },
      {
        id: 'organizationName',
        label: t('Organization'),
        getValue(item): ReactElement {
          return (
            <div key={`${item.id}_col_4`}>
              <Typography>{item.organizationName}</Typography>
            </div>
          );
        },
        sortKey: 'organizationName',
        sortable: true
      },
      {
        id: 'studentCount',
        label: t('No. of Students'),
        getValue(item): ReactElement {
          return (
            <div key={`${item.id}_col_5`}>
              <Typography>{item.studentCount}</Typography>
            </div>
          );
        },
        sortKey: 'studentCount',
        sortable: true
      },
      {
        id: 'status',
        label: t('Status'),
        getValue(item): ReactElement {
          return (
            <div key={`${item.id}_col_6`}>
              <StatusChip
                status={item.dateArchived ? ClassroomStatus.ARCHIVED : ClassroomStatus.ACTIVE}
                size="small"
              />
            </div>
          );
        },
        sortKey: 'status',
        sortable: true
      },
      {
        id: 'products',
        label: t('Products'),
        getValue(item: Classroom): ReactElement {
          return <ProductIconList style={{ minWidth: 120 }} licenseUsage={item.licenseUsage} />;
        },
        sortable: false
      },
      {
        id: 'actions',
        label: '',
        // eslint-disable-next-line react/display-name
        getValue(item: Classroom): ReactElement {
          return (
            <MoreVertMenu
              actionOptions={actionOptions ?? []}
              id={item.id}
              context={{ classroomId: item.id, organizationId: item.organization?.id }}
            />
          );
        },
        sortable: false
      }
    ],
    [actionOptions]
  );

  const buildRecord = useCallback((classroom: Classroom): any => {
    const educator = classroom.users?.find(({ classroomRoles }) =>
      classroomRoles?.find(role => role.classification === ClassroomRoleClassification.EDUCATOR)
    )?.user;
    const coeducators =
      classroom.users
        ?.filter(({ classroomRoles }) => {
          return classroomRoles?.find(
            role => role.classification === ClassroomRoleClassification.COEDUCATOR
          );
        })
        .map(({ user }) => `${user.firstName} ${user.lastName}`)
        .join(', ') || '';

    return {
      ...classroom,
      organizationName: classroom.organization?.name,
      coeducators,
      educator: educator ? `${educator.firstName} ${educator?.lastName}` : ''
    };
  }, []);

  const buildRowData = useCallback(() => classrooms.map(buildRecord), [classrooms, buildRecord]);
  const columns: ColumnProps<any>[] = getColumns(t);

  return (
    <div className={classes.container}>
      <LwtTable
        columns={columns}
        data={buildRowData()}
        options={{ checkbox: false, sortable: true }}
        onRowClick={(item): void => history.push(`/admin/classes-students/${item.id}`)}
        classes={{
          table: (): string => classes.table,
          body: (): string => classes.tableBody,
          row: (): string => classes.tableRow
        }}
        rowProps={(item, index) => ({
          style: {
            borderLeft: `10px solid ${item.color}`
          }
        })}
      />
    </div>
  );
};

export default ClassesAndStudentsListViewContainer;
